import React, { useEffect, useRef, useState, memo } from 'react';
import { Award, Heart, Globe, Users } from 'lucide-react';

const AboutSection = memo(() => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const values = [
    {
      icon: Globe,
      title: 'World Wide Shipping',
      description: 'We deliver our exquisite sarees to customers across the globe with care and precision.',
    },
    {
      icon: Heart,
      title: 'Handcrafted Love',
      description: 'Each saree is meticulously crafted by skilled artisans with generations of expertise.',
    },
    {
      icon: Award,
      title: 'Authentic Heritage',
      description: 'Preserving traditional weaving techniques while embracing contemporary designs.',
    },
    {
      icon: Users,
      title: 'Customer First',
      description: 'Dedicated to providing a personalized shopping experience for every customer.',
    },
  ];

  return (
    <section
      id="about"
      ref={sectionRef}
      className="relative py-24 sm:py-32 overflow-hidden"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `radial-gradient(circle at 25% 25%, hsl(43 74% 49%) 1px, transparent 1px)`,
            backgroundSize: '50px 50px',
          }}
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span 
            className={`inline-block font-elegant text-primary text-lg tracking-widest uppercase mb-4 transition-all duration-700 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            Our Story
          </span>
          <h2 
            className={`text-4xl sm:text-5xl lg:text-6xl text-foreground mb-6 transition-all duration-700 delay-100 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
            style={{ fontFamily: 'Faith, Playfair Display, serif' }}
          >
            About <span className="text-gold-gradient">Shachi Collections</span>
          </h2>
          <div 
            className={`w-24 h-1 bg-primary mx-auto mb-8 transition-all duration-700 delay-200 ${
              isVisible ? 'opacity-100 scale-x-100' : 'opacity-0 scale-x-0'
            }`}
          />
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center mb-20">
          {/* Left - Image with decorative frame */}
          <div 
            className={`relative transition-all duration-1000 delay-300 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
            }`}
          >
            <div className="relative">
              {/* Decorative corner borders */}
              <div className="absolute -top-4 -left-4 w-24 h-24 border-t-2 border-l-2 border-primary/50" />
              <div className="absolute -bottom-4 -right-4 w-24 h-24 border-b-2 border-r-2 border-primary/50" />
              
              {/* Image */}
              <div className="relative overflow-hidden">
                <img
                  src="/assets/hero.webp"
                  alt="Traditional Saree Craftsmanship"
                  className="w-full aspect-[4/5] object-cover object-top hover-scale"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background/60 via-transparent to-transparent" />
              </div>
            </div>
          </div>

          {/* Right - Text Content */}
          <div 
            className={`transition-all duration-1000 delay-500 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
            }`}
          >
            <h3 className="font-display text-2xl sm:text-3xl text-foreground mb-6">
              A Legacy of <span className="text-primary">Timeless Craftsmanship</span>
            </h3>
            
            <div className="space-y-5 font-body text-foreground/70 leading-relaxed">
              <p>
                Nestled in the heart of India&apos;s textile heritage, Shachi Collections was 
                born from a deep reverence for the ancient art of saree making. What began as a 
                modest family endeavor has blossomed into a celebration of India&apos;s diverse weaving traditions.
              </p>
              
              <p>
                Every saree in our collection carries within it the whispers of master weavers—artisans 
                who have dedicated their lives to perfecting techniques passed down through generations. 
                From the lustrous Banarasi silks that shimmer like liquid gold to 
                the elegant Kanjivaram weaves that tell tales of South Indian 
                splendor, each piece is a testament to human artistry.
              </p>
              
              <p>
                We don&apos;t just sell sarees; we curate experiences. Each thread is woven with stories 
                of heritage, each motif carries centuries of symbolism, and each drape transforms 
                the wearer into a vision of ethereal grace. At Shachi Collections, 
                we believe that a saree is not merely attire—it&apos;s an heirloom, a memory, a piece of art.
              </p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-6 mt-8 pt-8 border-t border-primary/20">
              <div className="text-center">
                <p className="font-display text-3xl sm:text-4xl text-primary">500+</p>
                <p className="font-elegant text-sm text-foreground/60">Unique Designs</p>
              </div>
              <div className="text-center">
                <p className="font-display text-3xl sm:text-4xl text-primary">50+</p>
                <p className="font-elegant text-sm text-foreground/60">Master Artisans</p>
              </div>
              <div className="text-center">
                <p className="font-display text-3xl sm:text-4xl text-primary">500+</p>
                <p className="font-elegant text-sm text-foreground/60">Happy Customers</p>
              </div>
            </div>
          </div>
        </div>

        {/* Values Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {values.map((value, index) => (
            <div
              key={value.title}
              className={`relative p-6 glass rounded-sm border border-primary/10 hover:border-primary/30 transition-all duration-500 group ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${600 + index * 100}ms` }}
            >
              {/* Icon */}
              <div className="w-14 h-14 rounded-full glass-gold flex items-center justify-center mb-4 group-hover:glow-gold transition-all duration-300">
                <value.icon className="w-7 h-7 text-primary" />
              </div>
              
              <h4 className="font-display text-xl text-foreground mb-2">{value.title}</h4>
              <p className="font-body text-sm text-foreground/60 leading-relaxed">{value.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
});

AboutSection.displayName = 'AboutSection';

export default AboutSection;
